import ConfigProvider, { type ThemeConfig } from "antd/lib/config-provider";
import Divider from "antd/lib/divider";
import Layout from "antd/lib/layout";
import theme from "antd/lib/theme";
import Typography from "antd/lib/typography";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useActiveClassroom } from "@sol/classrooms";
import { Feature, useCheckFeatureFlag } from "@sol/features";
import { getResourceUuid, IUser } from "@sol/sdk";
import { Plus } from "@sol/uikit/core/icons/Plus";
import { Badge } from "@sol/uikit/data-display/Badge/Badge";
import { Menu as SOLMenu } from "@sol/uikit/navigation/Menu/Menu";
import { useRouteBasedMenuState } from "@sol/uikit/navigation/Menu/useRouteBasedMenuState";

import { SidebarLogo } from "./components/SidebarLogo";
import { SidebarQuickActions } from "./components/SidebarQuickActions";
import { SidebarUserDropdown } from "./components/SidebarUserDropdown";
import { useSidebarState } from "./hook/useSidebarState";
import { getDropdownItems } from "./lib/getDropdownItems";
import { getLearnerMenuItems } from "./lib/getLearnerMenuItems";
import { getMenuItems, MENU_OWNED_ROUTES } from "./lib/getMenuItems";
import { getQuickActionItems } from "./lib/getQuickActionItems";

export type SidebarProps = {
    currentUser?: IUser;
    logout: () => void;
};

const { Sider } = Layout;

export const Sidebar = ({ currentUser, logout }: SidebarProps) => {
    const { token } = theme.useToken();
    const [t] = useTranslation();
    const checkFeatureFlag = useCheckFeatureFlag();
    const { activeClassroom } = useActiveClassroom();
    const { menuState, isCollapsed, toggleCollapsed, handleMouseEnter, handleMouseLeave } = useSidebarState();

    const isLearner = checkFeatureFlag(Feature.MENU_LAYOUT_LEARNER);
    const classroomId = activeClassroom ? getResourceUuid(activeClassroom) : undefined;

    const menuItems = useMemo(
        () =>
            isLearner
                ? getLearnerMenuItems({ t, checkFeatureFlag, classroomId })
                : getMenuItems({ t, checkFeatureFlag, activeClassroom }),
        [isLearner, t, classroomId, checkFeatureFlag, activeClassroom],
    );
    const quickActionItems = useMemo(() => getQuickActionItems({ t, checkFeatureFlag }), [t, checkFeatureFlag]);
    const dropdownItems = useMemo(() => getDropdownItems({ t, logout }), [t, logout]);

    const quickActionMenuItems = useMemo(
        () => [
            {
                type: "group",
                label: t("component.Navigation.Menu.quickActionsMenu.title"),
                children: quickActionItems.map(item => {
                    if (!item || !("icon" in item) || item.icon) {
                        return item;
                    }

                    return { ...item, icon: <Plus size={16} className="rounded-none" /> };
                }),
            } as const,
        ],
        [quickActionItems, t],
    );

    const mainMenuState = useRouteBasedMenuState(menuItems, MENU_OWNED_ROUTES);
    const quickActionMenuState = useRouteBasedMenuState(quickActionMenuItems);

    const mainMenuThemeConfig: ThemeConfig = useMemo(
        () => ({
            cssVar: { key: "main-menu" },
            token: { colorPrimary: token.red },
        }),
        [token.red],
    );

    return (
        <Sider
            theme="light"
            className="flex min-h-full flex-col"
            trigger={null}
            collapsedWidth={50}
            collapsed={isCollapsed}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            width={menuState === "fixed" ? 200 : 50}
        >
            <div
                className={clsx([
                    isCollapsed ? "w-[50px]" : "w-[200px]",
                    "absolute top-0 z-50 flex h-full flex-col overflow-hidden bg-ant-container pt-6 transition-all duration-300 ease-in-out",
                ])}
            >
                <SidebarLogo menuState={menuState} onToggleCollapsed={toggleCollapsed} />

                <div className="overflow-x-hidden overflow-y-auto">
                    <ConfigProvider theme={mainMenuThemeConfig}>
                        <SOLMenu
                            data-cy="main-menu"
                            className="mt-4"
                            mode="inline"
                            inlineIndent={0}
                            items={menuItems}
                            rootClassName="!border-r-0 [&_.ant-menu-item]:cursor-pointer [&_.ant-menu-submenu-title]:cursor-pointer"
                            {...mainMenuState}
                            openKeys={undefined}
                            defaultOpenKeys={mainMenuState.openKeys}
                        />
                    </ConfigProvider>

                    <AnimatePresence>
                        {!isCollapsed && quickActionItems.length > 0 ? (
                            <motion.div
                                initial={{ opacity: 1 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="mt-3 w-[200px] overflow-hidden"
                            >
                                <Divider className="mt-0" />
                                <SidebarQuickActions
                                    className="mx-3 mb-3 w-auto"
                                    mode="inline"
                                    items={quickActionMenuItems}
                                    {...quickActionMenuState}
                                />
                            </motion.div>
                        ) : null}
                    </AnimatePresence>
                </div>

                <div className="mt-auto w-full overflow-hidden">
                    {!isCollapsed && isLearner && (
                        <div
                            className="w-full p-2"
                            title={
                                activeClassroom
                                    ? activeClassroom.title
                                    : t("component.Navigation.Menu.items.classroomSpace.children.noActiveClassroom")
                            }
                        >
                            <div className="flex items-center gap-1.5 rounded-md bg-ant-fill-quaternary px-2 py-1.5">
                                <Badge color={token.colorPrimary} />
                                <Typography.Text className="text-ant-xs flex-1 text-center" ellipsis>
                                    {activeClassroom
                                        ? activeClassroom.title
                                        : t(
                                              "component.Navigation.Menu.items.classroomSpace.children.noActiveClassroom",
                                          )}
                                </Typography.Text>
                            </div>
                        </div>
                    )}

                    <SidebarUserDropdown
                        currentUser={currentUser}
                        dropdownItems={dropdownItems}
                        menuState={menuState}
                    />
                </div>
            </div>
        </Sider>
    );
};
